import { formatMoney, type PizzaConfig } from '@agos/shared'

import type { PizzaSizeRow } from './pizza-size-rows'

/**
 * What the base contributes, per size — FRONTEND.md §9.227.
 *
 * *"in dynamická cena for pizza is missing základ, it should be there too, thats what is making the
 * price"* — the owner, on the dynamic-pricing block. Legacy prints exactly this under the sizes:
 * `Základ: 32 cm = 4,50 €, 50 cm = 7,00 €`, off the franchise's pizza configuration.
 *
 * **This is a reading, not a control.** The base price belongs to the pizza configuration and is
 * edited there; a product's dynamic price is built on top of it. Showing it here answers *where does
 * the number come from* without giving the product form a second place to change it.
 *
 * **Only the sizes this product offers.** A size the form has unticked is not part of what the
 * product costs, so listing its base would describe a price nobody can buy.
 */
export function basePriceNote(
  config: Pick<PizzaConfig, 'sizes'> | null,
  rows: readonly PizzaSizeRow[],
): string {
  if (config === null) return ''

  const offered = new Set(
    rows.filter((row) => row.offered !== false).map((row) => normaliseLabel(row.label)),
  )

  const parts = (config.sizes ?? [])
    .filter((size) => offered.has(normaliseLabel(size.label)))
    .map((size) => `${size.label} = ${formatMoney(size.basePrice)}`)

  return parts.join(', ')
}

/**
 * `32cm`, `32 cm` and `32 CM` are the same size.
 *
 * The configuration and the product's own rows are edited on different screens and the dumps carry
 * both spellings, so matching on the raw string would silently drop a size from the note — a missing
 * line rather than a wrong one, which is the harder kind to notice.
 */
function normaliseLabel(label: string | null | undefined): string {
  return (label ?? '').replace(/\s+/g, '').toLowerCase()
}
